package com.downdogapp.client.controllers

import com.downdogapp.client.MANIFEST
import com.downdogapp.client.Strings
import com.downdogapp.client.ViewController
import com.downdogapp.client.api.*
import com.downdogapp.client.api.LoginResponseType.*
import com.downdogapp.client.controllers.start.StartViewController
import com.downdogapp.client.singleton.AlertAction
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Network
import com.downdogapp.client.views.SignInView

class SignInViewController(val isSignUp: Boolean,
                           val titleLabel: String?,
                           val subtitleLabel: String?,
                           private val isReferral: Boolean = false,
                           private val requiredEmailDomain: String? = null,
                           private val onSuccess: (() -> Unit)) : ViewController() {

  val helper = SignInViewControllerHelper(this)
  override val view = SignInView(this)

  private val displayAppleSignInButton = MANIFEST.displayAppleSignInButton && helper.isAppleSignInAvailable

  val numThirdPartyButtons
    get() =
      (if (displayAppleSignInButton) 1 else 0) +
      (if (MANIFEST.displayGoogleSignInButton) 1 else 0) +
      (if (MANIFEST.displayFacebookSignInButton) 1 else 0)

  fun googleClicked() {
    logEvent("login_with_google")
    view.showSpinner()
    helper.signInWithGoogle()
  }

  fun facebookClicked() {
    logEvent("login_with_facebook")
    view.showSpinner()
    helper.signInWithFacebook()
  }

  fun appleClicked() {
    logEvent("login_with_apple")
    view.showSpinner()
    helper.signInWithApple()
  }

  fun submitClicked() {
    logEvent("login_with_email_password")
    App.closeKeyboard()
    val email = view.email.trim()
    val password = view.password
    if (!isValidEmail(email)) {
      App.alert(message = Strings.INVALID_EMAIL_MESSAGE)
    } else if (password.length < 4) {
      App.alert(message = Strings.PASSWORD_LENGTH_MESSAGE)
    } else {
      view.showSpinner()
      login(email = email, password = password)
    }
  }

  fun forgotPasswordClicked() {
    if (isValidEmail(view.email)) {
      view.showSpinner()
      Network.post(ForgotPasswordRequest(view.email)) {
        if (it != null) {
          val response = ForgotPasswordResponse.fromJson(it)
          App.alert(message = response.message)
        } else {
          App.alertNoInternet()
        }
        view.hideSpinner()
      }
    } else {
      App.alert(message = Strings.FORGOT_PASSWORD_NEEDS_EMAIL) {
        view.focusEmail()
      }
    }
  }

  fun facebookLogin(email: String, idToken: String?, firstName: String?, lastName: String?) =
    login(email = email, facebookIdToken = idToken, firstName = firstName, lastName = lastName)

  fun googleLogin(email: String, idToken: String?, firstName: String, lastName: String) =
    login(email = email, googleIdToken = idToken, firstName = firstName, lastName = lastName)

  fun appleLogin(idToken: String) =
    login(email = null, appleIdToken = idToken)

  fun onFacebookSignInError(errorMessage: String?) {
    logError("sign_in_failure", mapOf("platform" to "facebook", "message" to errorMessage))
    App.alert(title = Strings.LOGIN_ERROR, message = errorMessage) {
      onLoginFailed()
    }
  }

  fun onFacebookGraphRequestError(errorMessage: String?) {
    logError("facebook_graph_request_failure", mapOf("message" to errorMessage))
    App.alert(message = errorMessage ?: Strings.NO_INTERNET_MESSAGE) {
      onLoginFailed()
    }
  }

  fun onFacebookSignInResponse(email: String?, idToken: String, firstName: String?, lastName: String?) {
    if (email.isNullOrEmpty()) {
      App.alert(message = Strings.NO_FACEBOOK_EMAIL_MESSAGE) {
        onLoginFailed()
      }
    } else {
      facebookLogin(email, idToken, firstName, lastName)
    }
  }

  fun onGoogleSignInError(errorMessage: String?) {
    logError("sign_in_failure", mapOf("platform" to "google", "message" to errorMessage))
    App.alert(title = Strings.LOGIN_ERROR, message = errorMessage) {
      onLoginFailed()
    }
  }

  fun onAppleSignInError(errorMessage: String?) {
    logError("sign_in_failure", mapOf("platform" to "apple", "message" to errorMessage))
    App.alert(title = Strings.LOGIN_ERROR, message = errorMessage) {
      onLoginFailed()
    }
  }

  fun onLoginFailed() {
    App.logoutAccounts()
    view.hideSpinner()
  }

  private fun login(email: String?,
                    facebookIdToken: String? = null,
                    googleIdToken: String? = null,
                    appleIdToken: String? = null,
                    password: String? = null,
                    firstName: String? = null,
                    lastName: String? = null) {
    requiredEmailDomain?.let { domain ->
      val invalidEmail =
        email?.let {
          !it.substringAfter("@").equals(domain, ignoreCase = true)
        } ?: true

      if (invalidEmail) {
        App.alert(title = Strings.LOGIN_ERROR, message = Strings.REQUIRED_EMAIL_DOMAIN_ALERT(domain)) {
          view.hideSpinner()
        }
        return
      }
    }

    val loginRequest = LoginRequest(
      email = email,
      password = password,
      facebookIdToken = facebookIdToken,
      googleIdToken = googleIdToken,
      appleIdToken = appleIdToken
    )
    Network.post(loginRequest) { json ->
      if (json == null) {
        App.alertNoInternet {
          view.hideSpinner()
        }
      } else {
        val response = LoginResponse.fromJson(json)
        when (response.type) {
          SUCCESS -> {
            fun loginBlock() {
              logEvent("successfully_logged_in")
              App.requestNotificationsAuthorization()
              onLoginSuccessful(response.cred)
            }

            if (isReferral) {
              App.alert(message = Strings.ACCOUNT_INELIGIBLE_FOR_REFERRAL_MESSAGE) {
                App.goHomeOrOpenApp()
              }
            } else {
              loginBlock()
            }
          }

          ERROR -> {
            logError("login_error", mapOf("message" to response.message))
            App.alert(title = response.title, message = response.message!!) {
              view.hideSpinner()
            }
          }

          WRONG_PASSWORD -> {
            logEvent("incorrect_password")
            App.alert(
              title = response.title,
              message = response.message!!,
              negativeButton = AlertAction(Strings.TRY_AGAIN) {
                view.hideSpinner()
              },
              positiveButton = AlertAction(Strings.RESET) {
                logEvent("forgot_password")
                Network.post(ForgotPasswordRequest(email = email!!)) { j ->
                  if (j == null) {
                    App.alertNoInternet {
                      view.hideSpinner()
                    }
                  } else {
                    App.alert(message = Strings.RESET_PASSWORD_MESSAGE(email)) {
                      view.hideSpinner()
                    }
                  }
                }
              }
            )
          }

          SIGN_UP -> {
            fun signUpRequestBlock() {
              val signUpRequest = SignUpRequest(
                email = email,
                facebookIdToken = facebookIdToken,
                googleIdToken = googleIdToken,
                appleIdToken = appleIdToken,
                password = password,
                firstName = firstName,
                lastName = lastName
              )
              Network.post(signUpRequest) { j ->
                if (j == null) {
                  App.alertNoInternet {
                    view.hideSpinner()
                  }
                } else {
                  logEvent("sign_up")
                  onLoginSuccessful(SignUpResponse.fromJson(j).cred)
                }
              }
            }

            if (isSignUp) {
              signUpRequestBlock()
            } else {
              App.alert(
                title = response.title,
                message = response.message!!,
                negativeButton = AlertAction(Strings.CANCEL) {
                  logEvent("declined_creating_account")
                  view.hideSpinner()
                },
                positiveButton = AlertAction(Strings.YES_SIGN_UP, ::signUpRequestBlock)
              )
            }
          }
        }
      }
    }
  }

  private fun onLoginSuccessful(cred: String?) {
    cred?.let {
      App.cred = it
    }

    App.fetchManifest(loadStoredOnFailure = false) {
      view.hideSpinner()
      if (MANIFEST.onboardingConfig?.showBeforeLogin == false) {
        StartViewController.promptOnboarding {
          onSuccess()
        }
      } else {
        onSuccess()
      }
    }
  }

  override fun onBackClicked() {
    App.closeKeyboard()
    super.onBackClicked()
  }

  companion object {

    fun isValidEmail(email: String) =
      Regex("[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}").matches(email)
  }
}
